import { SparklesIcon } from '@heroicons/react/outline';
import { PlayIcon } from '@heroicons/react/solid';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useRef, useState } from 'react';

import { useSpecialOffer } from '@/utils/useSpecialOffer';
import AnthropicSvg from '~/svg/Anthropic.svg';
import DeepseekSvg from '~/svg/deepseek.svg';
import GoogleSvg from '~/svg/google.svg';
import HailuoSvg from '~/svg/Hailuo.svg';
import MoonshotSvg from '~/svg/moonshot.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import PerplexitySvg from '~/svg/perplexity.svg';
import QwenSvg from '~/svg/qwen.svg';
import XaiSvg from '~/svg/xai.svg';

import { HappyUsers } from './landing/HappyUsers';
import LandingPageNavigation from './layout/LandingPageNavigation';
import SpecialOfferLandingPageNavigation from './layout/SpecialOfferLandingPageNavigation';

const HeroSection = () => {
  // Static homepage doesn't need user context - always show "Get started for free"
  const user = false;
  const { t, lang } = useLocale('home');
  const { isSpecialOffer } = useSpecialOffer();
  const videoRef = useRef<HTMLVideoElement>(null);
  // Poster image is preloaded in _document.tsx, so we show it immediately
  const [isLoaded, setIsLoaded] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  // Track if video source has been loaded (prevents iOS Safari from preloading)
  const [videoLoaded, setVideoLoaded] = useState(false);

  const handlePlay = () => {
    if (videoRef.current) {
      // Load video source on first play (iOS Safari ignores preload='none')
      if (!videoLoaded) {
        setVideoLoaded(true);
        // Need to wait for source to be added before playing
        setTimeout(() => {
          videoRef.current?.load();
          videoRef.current?.play().catch(() => {});
          setHasStarted(true);
        }, 0);
        return;
      }
      videoRef.current.play().catch(() => {});
      setHasStarted(true);
    }
  };

  return (
    <div className='relative pt-32 pb-16 lg:pt-48 overflow-hidden'>
      {/* Navigation */}
      <div className='absolute top-0 left-0 w-full z-20'>
        {isSpecialOffer ? (
          <SpecialOfferLandingPageNavigation />
        ) : (
          <LandingPageNavigation variant='light' skipUserContext forceDark />
        )}
      </div>

      {/* Background Elements - hidden on mobile for performance */}
      <div className='hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none'>
        <div className='absolute top-[20%] left-[10%] w-72 h-72 bg-purple-500/20 rounded-full blur-[100px]' />
        <div className='absolute top-[30%] right-[10%] w-96 h-96 bg-indigo-500/20 rounded-full blur-[100px]' />
      </div>

      <div className='relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
        {isSpecialOffer && (
          <div className='mb-8 inline-flex items-center rounded-full border border-purple-500/30 bg-purple-500/10 px-4 py-1.5 text-sm font-medium text-purple-300 backdrop-blur-sm'>
            <span className='mr-2 rounded-full bg-purple-500 px-2 py-0.5 text-[10px] font-bold text-white'>
              NEW
            </span>
            Get 30% off. Use code:{' '}
            <b className='ml-1 text-white'>TRYEASYPEASYAI</b>
          </div>
        )}

        <h1
          className='text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-white mb-8 drop-shadow-2xl'
          style={{ lineHeight: 1.1 }}
        >
          {t('One AI platform.')}{' '}
          <span className='text-purple-300'>{t('Every task.')}</span>
        </h1>
        <div className='mx-auto text-slate-300 text-lg md:text-2xl font-medium max-w-4xl leading-relaxed mb-10'>
          {t(
            'AI For Every Task You Have.  Work smarter, create faster, achieve more.',
          )}
        </div>

        {/* AI Provider Logos */}
        <div className='relative w-full overflow-hidden mb-10'>
          <div className='flex w-max items-center gap-x-8 animate-scroll-logos'>
            {[false, true].map((isDuplicate) => (
              <React.Fragment key={isDuplicate ? 'dup' : 'main'}>
                {(
                  [
                    {
                      name: 'Claude',
                      Icon: AnthropicSvg,
                    },
                    {
                      name: 'OpenAI',
                      Icon: OpenAISvg,
                    },
                    {
                      name: 'Perplexity',
                      Icon: PerplexitySvg,
                    },
                    {
                      name: 'Nano Banana Pro',
                      viewBox: '0 0 340 340',
                      path: 'M0 170C0 76.3 76.3 0 170 0c37.8 0 73.7 12.2 103.7 35.2l-39.5 51.3c-18.6-14.3-40.8-21.8-64.2-21.8-58.1 0-105.3 47.2-105.3 105.3S111.9 275.3 170 275.3c46.8 0 86.4-30.7 100.2-72.9H170v-64.7h170V170c0 93.7-76.3 170-170 170S0 263.7 0 170',
                    },
                    {
                      name: 'Gemini',
                      Icon: GoogleSvg,
                    },
                    {
                      name: 'Runway',
                      viewBox: '0 0 24 24',
                      path: 'M20.678 14.063 17.99 11.38c5.666-.768 5.122-9.298-.61-9.298-2.358-.003-8.2.003-10.628 0-1.611-.018-3.192.878-4.011 2.266a4.7 4.7 0 0 0-.661 2.396c0 2.33.003 8.208 0 10.615-.005 5.726 8.526 6.262 9.31.616 1.55 1.437 3.572 4.27 5.99 4.05h-.002c4.038.123 6.254-5.183 3.3-7.96ZM8.594 17.36c.036 2.394-3.73 2.386-3.692 0V6.777A1.86 1.86 0 0 1 6.23 4.974c1.138-.367 2.396.577 2.364 1.768zM11.05 4.9h6.33c2.391-.039 2.394 3.723 0 3.684h-5.964c-.008-1.096.114-2.672-.366-3.684m5.027 13.764-.629-.632-4.029-4.022v-2.606h2.607l4.66 4.654c1.717 1.664-.944 4.323-2.61 2.606Z',
                    },
                    {
                      name: 'ElevenLabs',
                      viewBox: '0 0 24 24',
                      path: 'M2 0h6v24H2zM16 0h6v24h-6z',
                    },
                    {
                      name: 'Kling',
                      viewBox: '0 0 24 24',
                      path: 'M21.973 15.22a287 287 0 0 1-2.218-1.758c-.4-.32-.825-.659-1.347-1.073 3.47-4.107 1.171-7.015-.336-8.523-1.883-1.884-4.869-2.44-7.824-1.922-3.49.613-6.526 2.806-8.331 6.02l-.317.562s3.7 2.937 4.357 3.455c-3.27 4.432-1.227 7.086-.223 8.091 1.477 1.477 3.952 2.296 6.207 2.296.62 0 1.256-.054 1.894-.166 3.49-.613 6.527-2.807 8.333-6.02l.317-.563-.507-.398zM5.264 9.508a317 317 0 0 0-1.706-1.355c1.608-2.515 4.11-4.224 6.948-4.722 1.782-.312 3.51-.112 4.96.548-3.776.91-7.148 3.758-8.802 6.645L5.262 9.507zm4.096-.265c2.787-3.17 6.368-4.578 7.995-3.146s.69 5.163-2.099 8.333c-2.787 3.17-6.368 4.577-7.995 3.146-1.627-1.433-.69-5.164 2.099-8.333m4.213 11.476c-1.783.313-3.509.113-4.96-.547 3.776-.91 7.149-3.758 8.801-6.644.548.435.988.785 1.402 1.116.534.427 1.046.835 1.704 1.355-1.608 2.516-4.109 4.225-6.947 4.722z',
                    },
                    {
                      name: 'Seedream',
                      viewBox: '0 0 32 32',
                      path: 'M5.915 25.44 1 26.702V4.311l4.915 1.263zm23.75 1.31-4.924 1.264V3l4.924 1.254zm-15.952-.627-4.915 1.264v-13.19l4.915 1.263zm3.21-13.883 4.925-1.264v13.19l-4.924-1.264z',
                    },

                    {
                      name: 'Flux',
                      viewBox: '0 0 490 345',
                      path: 'M349.391 147.163h-52.114l-52.113-73.418L82.594 302.52h52.227l110.341-155.352h52.113L186.935 302.52h52.369l110.087-155.355v73.417l-58.013 81.949v42.597h-30.154l-.001.002h-52.114l.001-.002h-52.439l-.004.006h-52.113l.004-.006H0L245.164 0zM490 345.13h-52.114l-.001-.002h-30.156v-42.44l-58.338-82.106v-73.417z',
                    },
                    {
                      name: 'Ideogram',
                      viewBox: '0 0 12 12',
                      path: 'M8.884.74a3.2 3.2 0 0 1 1.494 1.89 1.7 1.7 0 0 1 .627.398 1.7 1.7 0 0 1 .494 1.23 1.7 1.7 0 0 1-.546 1.207 1.69 1.69 0 0 1 .546 1.315 1.69 1.69 0 0 1-.654 1.264l-.018.015.01.016c.195.3.293.662.267 1.032l-.004.055a1.69 1.69 0 0 1-1.56 1.514 1.7 1.7 0 0 1-.77-.125 1.689 1.689 0 0 1-2.922.485 2 2 0 0 1-.818.199l-.062.004h-.449l-.034-.002c-.558-.033-.546-.887.034-.887h.402l.023-.002h.024a1.15 1.15 0 0 0 .039-2.3l-.04.002H1.404a.445.445 0 0 1 0-.89h3.517l.023-.001h.024a1.15 1.15 0 0 0 .254-2.273l-.07-.015a1 1 0 0 0-.15-.023l-.035.001H1.404a.445.445 0 1 1 0-.888h3.518l.023-.002h.023a1.15 1.15 0 1 0 .044-2.3l-.044.001h-.483c-.558-.034-.547-.888.034-.888h.4L4.968.77q.301.001.578.083l.015-.01A3.2 3.2 0 0 1 8.885.74M7.016 6.484l-.034.018-.042.025a2.04 2.04 0 0 1-.702 1.077 2.04 2.04 0 0 1 .769 1.594 2.03 2.03 0 0 1-.46 1.29.8.8 0 0 0 .97.233.8.8 0 0 0 .442-.894l-.015-.062-.007-.04-.002-.028-.001-.02v-.03l.003-.026.007-.04.009-.035.01-.032.018-.04.003-.005a.4.4 0 0 1 .079-.108l.027-.026.02-.016.026-.018.02-.013.028-.015.037-.015.028-.01.02-.005.039-.008.036-.005h.067l.029.003.028.005.031.008.016.005.024.008.027.012.022.01.027.016.017.011q.045.031.082.074l.019.023.017.024a.8.8 0 1 0 1.2-1.035 2 2 0 0 1-.237.003l-.028-.002-.013-.003h-.008A1.69 1.69 0 0 1 8.17 7.107l-.013-.058a.8.8 0 0 0-1.142-.566M4.808 8.78a.444.444 0 1 1 0 .889H2.97a.445.445 0 0 1 0-.889zM9.412 3.53a.8.8 0 0 0-.399.65l-.001.042a.445.445 0 0 1-.415.444h-.03a.445.445 0 0 1-.443-.414l-.001-.03a.8.8 0 0 0-1.264-.65 2.04 2.04 0 0 1-.627.837c.366.29.623.697.725 1.154A1.687 1.687 0 0 1 9.02 6.828l.01.046a.8.8 0 0 0 .697.63l.062.005.021.001q.045 0 .09-.005l.043-.005.03-.008a.8.8 0 0 0 .632-.844.8.8 0 0 0-.754-.737l-.04-.001a.44.44 0 0 1-.341-.16l-.017-.022a.44.44 0 0 1-.077-.351.445.445 0 0 1 .404-.355h.032a.8.8 0 0 0 .567-1.365.8.8 0 0 0-.393-.213l-.052-.01-.037-.007a.8.8 0 0 0-.486.102m-4.6 2.04a.445.445 0 1 1 0 .889H.445a.445.445 0 0 1 0-.889zm-.005-3.216a.444.444 0 1 1 0 .888H2.97a.445.445 0 1 1 0-.888zm1.649-.977-.027.01c.313.32.51.736.563 1.18a1.69 1.69 0 0 1 1.576.514 1.7 1.7 0 0 1 .847-.5 2.31 2.31 0 0 0-2.96-1.204',
                    },
                    {
                      name: 'Sora',
                      viewBox: '0 0 24 24',
                      path: 'M22.282 9.821a5.99 5.99 0 0 0-.516-4.91 6.05 6.05 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.99 5.99 0 0 0-3.998 2.9 6.05 6.05 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.05 6.05 0 0 0 6.515 2.9A5.99 5.99 0 0 0 13.26 24a6.06 6.06 0 0 0 5.772-4.206 6 6 0 0 0 3.997-2.9 6.06 6.06 0 0 0-.747-7.073zM13.26 22.43a4.48 4.48 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.8.8 0 0 0 .392-.681v-6.737l2.02 1.168a.07.07 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494M3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.77.77 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646M2.34 7.896a4.5 4.5 0 0 1 2.366-1.973V11.6a.77.77 0 0 0 .388.677l5.815 3.354-2.02 1.168a.08.08 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855-5.833-3.387L15.119 7.2a.08.08 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023-.141-.085-4.774-2.782a.78.78 0 0 0-.785 0L9.409 9.23V6.897a.07.07 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.8.8 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5Z',
                    },
                    {
                      name: 'Kimi',
                      Icon: MoonshotSvg,
                    },
                    {
                      name: 'DeepSeek',
                      Icon: DeepseekSvg,
                    },
                    {
                      name: 'MiniMax',
                      Icon: HailuoSvg,
                    },
                    {
                      name: 'xAI',
                      Icon: XaiSvg,
                    },
                    {
                      name: 'Qwen',
                      Icon: QwenSvg,
                    },
                  ] as {
                    name: string;
                    viewBox?: string;
                    path?: string;
                    fillRule?: 'evenodd';
                    Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
                  }[]
                ).map((logo) => (
                  <div
                    key={`${isDuplicate ? 'dup-' : ''}${logo.name}`}
                    className='flex shrink-0 items-center gap-2 pr-8'
                    aria-hidden={isDuplicate || undefined}
                  >
                    {logo.Icon ? (
                      <logo.Icon
                        aria-hidden='true'
                        className='h-[18px] w-auto shrink-0 fill-white/50 [&_path]:fill-white/50'
                      />
                    ) : (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox={logo.viewBox}
                        width={16}
                        height={16}
                        aria-hidden='true'
                        className='h-[18px] w-auto shrink-0 fill-white/50'
                      >
                        <path
                          d={logo.path}
                          fillRule={logo.fillRule}
                          clipRule={logo.fillRule}
                        />
                      </svg>
                    )}
                    <span className='shrink-0 text-base font-normal text-white/50'>
                      {logo.name}
                    </span>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
          <div className='pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-slate-900 to-transparent' />
          <div className='pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-slate-900 to-transparent' />
        </div>

        <div className='flex flex-col sm:flex-row items-center justify-center gap-4 mb-4'>
          <Link
            href={!user ? '/login#auth-sign-up' : '/dashboard'}
            className='group relative min-w-[200px] px-8 h-[60px] text-white bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-center font-bold hover:bg-white/20 transition-all flex items-center justify-center gap-3 text-lg overflow-hidden shadow-lg shadow-purple-500/20'
          >
            <div className='absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-600 opacity-80 group-hover:opacity-100 transition-opacity' />
            <SparklesIcon className='h-6 w-6 flex-shrink-0 relative z-10' />
            <span className='relative z-10'>
              {!user ? t('Get started for free') : t('Go to dashboard')}
            </span>
          </Link>
          {/* <Link
            href='#features'
            className='min-w-[160px] px-8 h-[60px] text-white text-center border border-white/10 bg-white/5 backdrop-blur-sm rounded-full hover:bg-white/10 hover:border-white/30 transition-all font-semibold flex items-center justify-center text-lg'
          >
            {t('Explore Features')}
          </Link> */}
        </div>

        {/* Happy Users */}
        <div className='flex justify-center'>
          <HappyUsers fromLandingPage={true} className='items-center' />
        </div>
      </div>

      <div className='relative mt-16 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='relative rounded-2xl bg-gray-900/50 border border-white/10 p-2 sm:p-4 ring-1 ring-white/10 backdrop-blur-xl shadow-2xl'>
          {/* Fixed aspect ratio container to prevent CLS */}
          <div
            className='rounded-xl overflow-hidden bg-gray-900 aspect-video relative shadow-inner'
            style={{ aspectRatio: '16/9' }}
          >
            {/* Loading skeleton with explicit dimensions matching video aspect ratio */}
            {!isLoaded && (
              <div
                className='absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center'
                aria-hidden='true'
              >
                <div className='w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin' />
              </div>
            )}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={videoRef}
              className={`w-full h-full object-cover transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
              playsInline
              // Don't preload - source is only added when user clicks play
              preload='none'
              poster='https://media.easy-peasy.ai/assets/placeholder_home_page.jpeg'
              controls={hasStarted}
              // Explicit dimensions to prevent CLS (16:9 aspect ratio)
              width={1920}
              height={1080}
            >
              {/* Only add source when user clicks play - prevents iOS Safari from preloading */}
              {videoLoaded && (
                <source
                  src='https://media.easy-peasy.ai/assets/homepage.webm'
                  type='video/webm'
                />
              )}
            </video>
            {/* Centered play button overlay */}
            {!hasStarted && isLoaded && (
              <button
                onClick={handlePlay}
                className='absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-all hover:from-black/70 hover:via-black/30'
                aria-label='Play video'
              >
                <div className='flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-2xl shadow-purple-500/50 transition-all hover:scale-110 hover:shadow-purple-500/70 ring-4 ring-white/20'>
                  <PlayIcon className='h-12 w-12 text-white ml-1 drop-shadow-lg' />
                </div>
              </button>
            )}
          </div>
        </div>
        {/* Glow effect behind video - hidden on mobile for performance */}
        <div className='hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-gradient-to-r from-purple-500/30 to-indigo-500/30 opacity-50 blur-3xl -z-10 rounded-[3rem]' />
      </div>
    </div>
  );
};

export default HeroSection;
